import { useState } from 'react';
import { fmt } from '@nyce/admin-kit';
import { Button, Dialog, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import type { GlobalSettings, Organization, OrganizationInput } from '../../api/setup';
import { LimitMeter } from '../../shared';
import { CustomerDocuments } from '../knowledge/CustomerDocuments';
import { SkillToggles } from '../knowledge/SkillToggles';
import { FeatureFlagsEditor, ModelSettingsEditor, flagOn } from './settings';
import {
  EnabledChip,
  SwitchRow,
  UsageLink,
  messageOf,
  useQueryParam,
  useQueryPatch,
  useSetup,
} from './shared';

type Tab = 'general' | 'features' | 'settings' | 'skills' | 'documents';
const TABS: { value: Tab; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'features', label: 'Features' },
  { value: 'settings', label: 'Settings' },
  { value: 'skills', label: 'Skills' },
  { value: 'documents', label: 'Documents' },
];
const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;

const empty: OrganizationInput = { id: '', name: '', enabled: true, settings: {}, features: {} };
const toInput = (o: Organization): OrganizationInput => ({
  id: o.id,
  name: o.name,
  enabled: o.enabled,
  settings: o.settings ?? {},
  features: o.features ?? {},
});

function OrganizationEditor({
  initial,
  isNew,
  global,
  canWrite,
  onSave,
  onClose,
}: {
  initial: OrganizationInput;
  isNew: boolean;
  global: GlobalSettings | null;
  canWrite: boolean;
  onSave: (o: OrganizationInput) => Promise<void>;
  onClose: () => void;
}) {
  const { target } = useSetup();
  const [v, setV] = useState(initial);
  const [tabRaw, setTab] = useQueryParam('tab');
  const tab = (TABS.some((t) => t.value === tabRaw) ? tabRaw : 'general') as Tab;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ro = !canWrite;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ ...v, id: v.id.trim(), name: v.name.trim() });
      onClose();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const idError = isNew && v.id && !SLUG.test(v.id) ? 'lowercase letters, digits, - and _' : undefined;
  const effective = { ...(global?.features ?? {}), ...(v.features ?? {}) };
  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? 'New organization' : `Organization ${v.name || v.id}`}
      maxWidth={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {ro ? 'Close' : 'Cancel'}
          </Button>
          {!ro && (
            <Button
              onClick={() => void save()}
              loading={busy}
              disabled={!v.id.trim() || !v.name.trim() || !!idError}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      <ErrorBox error={error} />
      <Tabs value={tab} onChange={setTab} options={TABS} />
      {tab === 'general' && (
        <div className="nyk-form">
          <TextField
            label="Id"
            value={v.id}
            onChange={(e) => setV({ ...v, id: e.target.value })}
            disabled={!isNew || ro}
            error={idError}
            hint="the AUTH customer id (the orgid claim); cannot be changed"
            required
          />
          <TextField
            label="Name"
            value={v.name}
            onChange={(e) => setV({ ...v, name: e.target.value })}
            disabled={ro}
            required
          />
          <SwitchRow
            label={v.enabled ? 'Enabled' : 'Disabled: its systems refuse sign-in'}
            checked={v.enabled}
            onChange={(enabled) => setV({ ...v, enabled })}
            disabled={ro}
          />
          <p className="nyk-ui-hint nyk-span2">
            The monthly NYCE token budget is set under Limits. Settings and features cascade global → tenant →
            organization → system.
          </p>
        </div>
      )}
      {tab === 'features' && (
        <FeatureFlagsEditor
          value={v.features ?? {}}
          onChange={(features) => setV({ ...v, features })}
          inherited={global?.features ?? {}}
          disabled={ro}
        />
      )}
      {tab === 'settings' && (
        <ModelSettingsEditor
          value={v.settings ?? {}}
          onChange={(settings) => setV({ ...v, settings })}
          inherited={global?.settings ?? {}}
          disabled={ro}
        />
      )}
      {(tab === 'skills' || tab === 'documents') && isNew && (
        <EmptyState title="Save the organization first." />
      )}
      {tab === 'skills' && !isNew && <SkillToggles target={target} scope="organization" scopeId={v.id} />}
      {tab === 'documents' && !isNew && (
        <>
          {!flagOn(effective, 'customerDocs') && (
            <p className="nyk-notice nyk-notice-warn bd-tab-hint">
              The Customer documents feature is off for this organization: the chat does not search these
              documents until it is switched on (Features).
            </p>
          )}
          <CustomerDocuments target={target} organizationId={v.id} />
        </>
      )}
    </Dialog>
  );
}

/** Organizations = AUTH customers; budgets, statistics and the settings/feature cascade roll up here. */
export function BuddyOrganizationsPage() {
  const { target, api } = useSetup();
  const canWrite = useBuddyWrite();
  const orgs = useAsync(() => api.organizations.list(), [api]);
  const global = useAsync(() => api.globalSettings.get().catch(() => null), [api]);
  const [edit] = useQueryParam('edit');
  const patch = useQueryPatch();
  const toast = useNotice();
  const open = (id: string) => patch({ edit: id, tab: null });
  const close = () => patch({ edit: null, tab: null });
  const isNew = edit === 'new' && canWrite;
  const editing = isNew ? empty : (orgs.data ?? []).find((o) => o.id === edit);

  const save = async (o: OrganizationInput) => {
    if (isNew) await api.organizations.create(o);
    else await api.organizations.update(o.id, o);
    toast.success(`Saved ${o.name}`);
    await orgs.reload();
  };
  const remove = async (o: Organization) => {
    try {
      await api.organizations.remove(o.id);
      toast.success(`Deleted ${o.name}`);
      await orgs.reload();
    } catch (e) {
      toast.error(`Could not delete ${o.name}: ${messageOf(e)}`);
    }
  };

  return (
    <Page
      title="Organizations"
      description="AUTH customers. A system's budget and statistics roll up to its organization; settings and features cascade global → tenant → organization → system."
      actions={canWrite && <Button onClick={() => open('new')}>New organization</Button>}
    >
      <ErrorBox error={orgs.error} />
      {toast.element}
      <Loading loading={orgs.loading && !orgs.data} />
      {orgs.data && !orgs.data.length && (
        <EmptyState
          card
          title="No organizations yet."
          hint="Systems work without one; an organization shares a budget and settings across systems."
        />
      )}
      {!!orgs.data?.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="organizations-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Status</th>
                  <th className="nyk-ui-num">Tenants</th>
                  <th className="nyk-ui-num">Systems</th>
                  <th className="nyk-ui-num">Monthly limit</th>
                  <th>Used this month</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orgs.data.map((o) => (
                  <tr key={o.id} data-organization={o.id}>
                    <td className="nyk-card-title">
                      <b>{o.name}</b>
                      <div className="nyk-ui-hint">{o.id}</div>
                    </td>
                    <td data-label="Status">
                      <EnabledChip enabled={o.enabled} />
                    </td>
                    <td className="nyk-ui-num" data-label="Tenants">
                      {o.tenantCount ?? 0}
                    </td>
                    <td className="nyk-ui-num" data-label="Systems">
                      {o.systemCount ?? 0}
                    </td>
                    <td className="nyk-ui-num" data-label="Monthly limit">
                      {o.monthlyLimit && !o.isUnlimited ? fmt(o.monthlyLimit) : 'unlimited'}
                    </td>
                    <td data-label="Used this month">
                      <span className="nyk-ui-hint">{fmt(o.usedTokens ?? 0)}</span>{' '}
                      <LimitMeter used={o.usedTokens} limit={o.monthlyLimit} unlimited={o.isUnlimited} />
                    </td>
                    <td className="nyk-row-actions">
                      <UsageLink kind="organization" id={o.id} target={target} />
                      <Button size="sm" variant="ghost" onClick={() => open(o.id)}>
                        {canWrite ? 'Edit' : 'View'}
                      </Button>
                      {canWrite && (
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Delete organization ${o.name}? Its systems keep working without an organization.`}
                          onConfirm={() => void remove(o)}
                        >
                          Delete
                        </ConfirmButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <OrganizationEditor
          key={isNew ? 'new' : editing.id}
          initial={isNew ? empty : toInput(editing as Organization)}
          isNew={isNew}
          global={global.data ?? null}
          canWrite={canWrite}
          onSave={save}
          onClose={close}
        />
      )}
    </Page>
  );
}
